import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Users, TrendUp, MapPin, WarningCircle, EnvelopeSimple } from "@phosphor-icons/react";
import { log } from "@/lib/logger";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [failures, setFailures] = useState([]);
  useEffect(() => {
    api.get("/leads/stats").then(r => setStats(r.data)).catch((err) => log.error("stats:", err));
    api.get("/leads").then(r => setRecent(r.data.slice(0, 6))).catch((err) => log.error("recent leads:", err));
    api.get("/email_log/stats").then(r => setEmailStats(r.data)).catch((err) => log.error("email stats:", err));
    api.get("/email_log", { params: { status: "failed", limit: 5 } }).then(r => setFailures(r.data)).catch((err) => log.error("email failures:", err));
    // `api` is a module-scoped axios instance and state setters are stable — no deps needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div data-testid="admin-overview">
      <div className="eyebrow text-solar mb-2">Dashboard</div>
      <h1 className="mb-2 text-3xl">Overview</h1>
      <p className="text-white/60 text-sm mb-8">Latest leads and pipeline summary.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Total Leads" value={stats?.total ?? "—"} icon={Users} />
        <StatCard label="New Leads" value={stats?.new ?? "—"} icon={TrendUp} />
        <StatCard label="Last 7 Days" value={stats?.last_7_days ?? "—"} icon={TrendUp} />
        <StatCard label="States Represented" value={stats?.by_state?.length ?? "—"} icon={MapPin} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        <BreakdownCard title="By State" rows={stats?.by_state?.map(r => ({ label: r.state, value: r.count })) || []} />
        <BreakdownCard title="By Industry" rows={stats?.by_industry?.map(r => ({ label: r.industry, value: r.count })) || []} />
        <div className="border border-chrome bg-steel p-6">
          <div className="eyebrow text-solar mb-3">Quick Actions</div>
          <div className="space-y-2">
            <Link to="/admin/leads" className="block text-sm text-white/85 hover:text-solar">→ View all leads</Link>
            <Link to="/admin/systems" className="block text-sm text-white/85 hover:text-solar">→ Manage systems</Link>
            <Link to="/admin/articles" className="block text-sm text-white/85 hover:text-solar">→ Publish an article</Link>
            <Link to="/admin/settings" className="block text-sm text-white/85 hover:text-solar">→ Business settings</Link>
          </div>
        </div>
      </div>

      <div className="border border-chrome bg-steel">
        <div className="p-4 border-b border-chrome flex items-center justify-between">
          <div className="eyebrow text-solar">Recent Leads</div>
          <Link to="/admin/leads" className="text-xs uppercase tracking-widest text-solar font-bold">See all →</Link>
        </div>
        <div className="divide-y divide-chrome">
          {recent.map((l) => (
            <div key={l.id} className="p-4 flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold">{l.first_name} {l.last_name} · <span className="text-white/60">{l.company || "—"}</span></div>
                <div className="text-xs text-white/60">{l.email} · {l.city}, {l.state} · {l.site_type}</div>
              </div>
              <div className="text-xs text-white/50">{(l.created_at || "").substring(0, 10)}</div>
            </div>
          ))}
          {recent.length === 0 && <div className="p-6 text-white/60 text-sm">No leads yet. Test the quote form to see one here.</div>}
        </div>
      </div>

      {/* Email delivery health */}
      <div className={`mt-8 border ${(emailStats?.failed_last_7d || 0) > 0 ? "border-red-500/60" : "border-chrome"} bg-steel`}>
        <div className="p-4 border-b border-chrome flex items-center justify-between">
          <div className="eyebrow text-solar flex items-center gap-2"><EnvelopeSimple size={14} weight="fill" /> Email Delivery</div>
          <div className="text-xs text-white/60">Total: {emailStats?.total ?? "—"} · Failed (7d): <span className={(emailStats?.failed_last_7d || 0) > 0 ? "text-red-400 font-bold" : "text-white/80"}>{emailStats?.failed_last_7d ?? "—"}</span></div>
        </div>
        <div className="divide-y divide-chrome">
          {failures.length === 0 ? (
            <div className="p-4 text-white/60 text-sm">No recent email failures.</div>
          ) : failures.map((f) => (
            <div key={f.id} className="p-4 flex items-start gap-3">
              <WarningCircle size={18} weight="fill" className="text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white">{f.kind} · <span className="text-white/70">{f.recipient}</span></div>
                <div className="text-xs text-red-300 break-words">{f.error}</div>
                <div className="text-[10px] text-white/50 mt-1">{f.created_at}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-chrome bg-steel p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow text-solar">{label}</div>
        <Icon size={18} weight="fill" className="text-solar" />
      </div>
      <div className="font-display text-4xl font-black">{value}</div>
    </div>
  );
}
function BreakdownCard({ title, rows }) {
  return (
    <div className="border border-chrome bg-steel p-6">
      <div className="eyebrow text-solar mb-3">{title}</div>
      {rows.length === 0 ? <div className="text-white/60 text-sm">No data yet.</div> : (
        <ul className="space-y-2">
          {rows.slice(0, 8).map((r) => (
            <li key={r.label} className="flex items-center justify-between text-sm">
              <span className="text-white/85">{r.label}</span>
              <span className="font-bold text-solar">{r.value}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
